// src/components/ProfitLossProfessional.jsx
import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import MenuBar from "../components/MenuBar";
import HeaderBar from "../components/HeaderBar";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
  CartesianGrid,
} from "recharts";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

/* -------------------- helpers -------------------- */
const DEFAULTS = {
  invoiceCollection: "sales_import_months",
  voucherCollection: "vouchers",
  debounceMs: 250,
  pdfScale: 2,
  pdfOrientation: "landscape",
  pdfPageSize: "a4",
  saveSettingsKey: "plp_settings_v1",
};

const money = (v) =>
  (Number(v) || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const parseAmount = (x) => {
  if (x == null || x === "") return 0;
  const n = Number(x);
  if (!isNaN(n)) return n;
  return Number(String(x).replace(/,/g, "")) || 0;
};

const formatDate = (d) => {
  if (!d) return "";
  if (typeof d.toDate === "function") {
    return d.toDate().toISOString().slice(0, 10);
  }
  const parsed = new Date(d);
  if (!isNaN(parsed)) {
    return parsed.toISOString().slice(0, 10);
  }
  return ""; // fallback for invalid values
};

const presetRanges = {
  "This Month": () => {
    const t = new Date();
    return [new Date(t.getFullYear(), t.getMonth(), 1), new Date()];
  },
  "Last Month": () => {
    const t = new Date();
    const last = new Date(t.getFullYear(), t.getMonth() - 1, 1);
    return [last, new Date(last.getFullYear(), last.getMonth() + 1, 0)];
  },
  "This FY": () => {
    const t = new Date();
    const startYear = t.getMonth() >= 3 ? t.getFullYear() : t.getFullYear() - 1;
    return [new Date(startYear, 3, 1), new Date(startYear + 1, 2, 31)];
  },
};

// dynamic debounce hook
function useDebounce(value, delay) {
  const [deb, setDeb] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDeb(value), Math.max(0, Number(delay) || 0));
    return () => clearTimeout(id);
  }, [value, delay]);
  return deb;
}

/* -------------------- main component -------------------- */
export default function ProfitLossProfessional({
  invoiceCollection = DEFAULTS.invoiceCollection,
  voucherCollection = DEFAULTS.voucherCollection,
}) {
  // filters & states
  const today = new Date();
  const defaultFrom = new Date(today.getFullYear(), today.getMonth(), 1);
  const [fromDate, setFromDate] = useState(defaultFrom.toISOString().slice(0, 10));
  const [toDate, setToDate] = useState(today.toISOString().slice(0, 10));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // raw docs
  const [invoices, setInvoices] = useState([]);
  const [vouchers, setVouchers] = useState([]);

  // drilldown + search
  const [expandedAccount, setExpandedAccount] = useState(null);
  const [searchAccount, setSearchAccount] = useState("");

  // settings
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [debounceMs, setDebounceMs] = useState(DEFAULTS.debounceMs);
  const [pdfScale, setPdfScale] = useState(DEFAULTS.pdfScale);
  const [pdfOrientation, setPdfOrientation] = useState(DEFAULTS.pdfOrientation);
  const [pdfPageSize, setPdfPageSize] = useState(DEFAULTS.pdfPageSize);
  const [persistSettings, setPersistSettings] = useState(true);

  // refs
  const reportRef = useRef(null);
  const statusRef = useRef(null);

  // restore settings
  useEffect(() => {
    try {
      const raw = localStorage.getItem(DEFAULTS.saveSettingsKey);
      if (raw) {
        const obj = JSON.parse(raw);
        if (obj) {
          if (obj.debounceMs !== undefined) setDebounceMs(obj.debounceMs);
          if (obj.pdfScale !== undefined) setPdfScale(obj.pdfScale);
          if (obj.pdfOrientation) setPdfOrientation(obj.pdfOrientation);
          if (obj.pdfPageSize) setPdfPageSize(obj.pdfPageSize);
          if (obj.persistSettings !== undefined) setPersistSettings(obj.persistSettings);
        }
      }
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (!persistSettings) return;
    try {
      const payload = { debounceMs, pdfScale, pdfOrientation, pdfPageSize, persistSettings };
      localStorage.setItem(DEFAULTS.saveSettingsKey, JSON.stringify(payload));
    } catch {
      /* ignore */
    }
  }, [debounceMs, pdfScale, pdfOrientation, pdfPageSize, persistSettings]);

  // debounced search
  const debouncedSearch = useDebounce(searchAccount, debounceMs);

  // robust toMillis
  const toMillis = (doc) => {
    if (!doc) return null;
    const tryParse = (d) => {
      if (d == null) return null;
      if (typeof d === "number") return d;
      if (typeof d === "string") {
        const p = Date.parse(d);
        if (!isNaN(p)) return p;
        const dd = new Date(d + "T00:00:00");
        if (!isNaN(dd)) return dd.getTime();
        return null;
      }
      if (typeof d === "object") {
        if (typeof d.toDate === "function") {
          try {
            const dt = d.toDate();
            if (dt instanceof Date && !isNaN(dt)) return dt.getTime();
          } catch {}
        }
        if (d.seconds) return d.seconds * 1000;
        if (d._seconds) return d._seconds * 1000;
      }
      return null;
    };

    const candidates = [doc.date, doc.txnDate, doc.voucherDate, doc.createdAt, doc.created, doc.timestamp];
    for (let c of candidates) {
      const val = tryParse(c);
      if (val) return val;
    }
    return null;
  };

  // fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const invSnap = await getDocs(collection(db, invoiceCollection));
      const vSnap = await getDocs(collection(db, voucherCollection));
      const allInv = invSnap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
      const allV = vSnap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));

      const fromTs = new Date(fromDate + "T00:00:00").getTime();
      const toTs = new Date(toDate + "T23:59:59").getTime();

      const invFiltered = allInv.filter((doc) => {
        const t = toMillis(doc);
        return t && t >= fromTs && t <= toTs;
      });

      const vFiltered = allV.filter((doc) => {
        const t = toMillis(doc);
        return t && t >= fromTs && t <= toTs;
      });

      setInvoices(invFiltered);
      setVouchers(vFiltered);

      if (statusRef.current) {
        statusRef.current.textContent = `Loaded ${invFiltered.length} invoices and ${vFiltered.length} vouchers`;
        setTimeout(() => {
          if (statusRef.current) statusRef.current.textContent = "";
        }, 2000);
      }
    } catch (err) {
      console.error(err);
      setError("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, invoiceCollection, voucherCollection]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // aggregation
  const aggregated = useMemo(() => {
    const incomeMap = new Map();
    const expenseMap = new Map();
    let totalIncome = 0;
    let totalExpense = 0;
    let totalCgst = 0;
    let totalSgst = 0;

    const addIncome = (acct, amt) => {
      incomeMap.set(acct, (incomeMap.get(acct) || 0) + amt);
      totalIncome += amt;
    };
    const addExpense = (acct, amt) => {
      expenseMap.set(acct, (expenseMap.get(acct) || 0) + amt);
      totalExpense += amt;
    };

    invoices.forEach((inv) => {
      const txnClass = (inv.transactionClass || inv.transactionclass || inv.transaction || "Sales").toString();
      const t = (inv.type || "").toString().toLowerCase();
      const totals = inv.totals || {};
      const gross = parseAmount(totals.gross ?? inv.gross ?? inv.amount ?? 0);
      const discount = parseAmount(totals.discount ?? inv.discount ?? 0);
      const cgst = parseAmount(totals.cgst ?? inv.cgst ?? 0);
      const sgst = parseAmount(totals.sgst ?? inv.sgst ?? 0);
      const revenue = Math.max(0, gross - discount);

      totalCgst += cgst;
      totalSgst += sgst;

      if (t === "outward" || /supply|sale/i.test(txnClass)) {
        addIncome("Sales", revenue);
      } else if (t === "inward" || /purchase|bill|expense/i.test(txnClass)) {
        addExpense("Purchases", revenue);
      } else {
        if ((inv.grandTotal ?? inv.total ?? 0) >= 0) addIncome("Sales", revenue);
        else addExpense("Other Expenses", revenue);
      }
    });

    vouchers.forEach((v) => {
      const rows = Array.isArray(v.rows) ? v.rows : [];
      rows.forEach((r) => {
        const drcr = (r.drcr || "").toString().toLowerCase();
        const debit = parseAmount(r.debit ?? r.Debit ?? 0);
        const credit = parseAmount(r.credit ?? r.Credit ?? 0);
        const label = r.partyName || r.account || r.ledger || r.narration || "Journal";

        if (drcr === "dr" && debit > 0) {
          addExpense(label, debit);
        } else if (drcr === "cr" && credit > 0) {
          addIncome(label, credit);
        } else {
          if (debit > credit) addExpense(label, debit);
          else if (credit > debit) addIncome(label, credit);
        }
      });
    });

    const income = Array.from(incomeMap.entries())
      .map(([account, amount]) => ({ account, amount }))
      .sort((a, b) => b.amount - a.amount);
    const expense = Array.from(expenseMap.entries())
      .map(([account, amount]) => ({ account, amount }))
      .sort((a, b) => b.amount - a.amount);

    const netProfit = totalIncome - totalExpense;
    return { income, expense, totalIncome, totalExpense, netProfit, totalCgst, totalSgst };
  }, [invoices, vouchers]);

  // master rows filtered by debounced search
  const masterRows = useMemo(() => {
    const all = [
      ...aggregated.income.map((r) => ({ ...r, type: "Income" })),
      ...aggregated.expense.map((r) => ({ ...r, type: "Expense" })),
    ];
    if (!debouncedSearch) return all;
    const q = debouncedSearch.toLowerCase();
    return all.filter((r) => r.account.toLowerCase().includes(q));
  }, [aggregated, debouncedSearch]);

  /* -------------------- exports -------------------- */
  const exportCsv = () => {
    const rows = [
      ["Type", "Account", "Amount"],
      ...aggregated.income.map((r) => ["Income", r.account, r.amount]),
      ["", "", ""],
      ...aggregated.expense.map((r) => ["Expense", r.account, r.amount]),
      ["", "", ""],
      ["Total Income", "", aggregated.totalIncome],
      ["Total Expense", "", aggregated.totalExpense],
      ["Net Profit", "", aggregated.netProfit],
      ["CGST", "", aggregated.totalCgst],
      ["SGST", "", aggregated.totalSgst],
    ];
    const csv = rows.map((r) => r.map((c) => `"${(c ?? "").toString().replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `profit-loss-${fromDate}-to-${toDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportPdf = async () => {
    if (!reportRef.current) {
      setError("Nothing to export");
      return;
    }
    setError("");
    try {
      const el = reportRef.current;
      const canvas = await html2canvas(el, {
        scale: Math.max(1, Number(pdfScale) || 1),
        useCORS: true,
        logging: false,
      });
      const pdf = new jsPDF({ orientation: pdfOrientation, unit: "pt", format: pdfPageSize });
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      const imgData = canvas.toDataURL("image/png");
      const imgProps = pdf.getImageProperties(imgData);
      const imgHeight = (imgProps.height * pdfWidth) / imgProps.width;

      let position = 0;
      while (position < canvas.height) {
        pdf.addImage(imgData, "PNG", 0, position ? 0 : 0, pdfWidth, imgHeight);
        position += pdfHeight;
        if (position < canvas.height) pdf.addPage();
      }

      pdf.save(`profit-loss-${fromDate}-to-${toDate}.pdf`);
    } catch (err) {
      console.error("PDF export failed", err);
      setError("PDF export failed — check console");
    }
  };

  const handlePrint = () => {
    if (!reportRef.current) return window.print();
    const w = window.open("", "_blank");
    if (!w) {
      setError("Unable to open print window");
      return;
    }
    w.document.write(
      `<html><head><title>Profit & Loss</title><style>body{font-family:Arial;padding:20px}</style></head><body>${reportRef.current.outerHTML}</body></html>`
    );
    w.document.close();
    w.focus();
    w.print();
  };

  const onPreset = (key) => {
    const [f, t] = presetRanges[key]();
    setFromDate(formatDate(f));
    setToDate(formatDate(t));
  };

  /* -------------------- render -------------------- */
  return (
    <div className="min-h-screen bg-[#f6f7f9]">
      <div className="fixed top-0 left-0 right-0 z-50">
        <MenuBar />
      </div>
      <div className="fixed top-7 left-0 right-0 z-40">
        <HeaderBar freeze={false} />
      </div>

      <main style={{ marginTop: 104 }} className="max-w-full">
        <div className="max-w-7xl mx-auto p-4 h-[calc(100vh-104px)] overflow-auto">
          {/* Controls row */}
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold text-slate-800">Profit & Loss — Professional</h1>
              <p className="text-sm text-slate-500">
                Period: {fromDate} — {toDate}
              </p>
              <div ref={statusRef} className="text-xs text-slate-400 mt-1"></div>
            </div>

            <div className="flex items-center gap-2">
              <button onClick={() => onPreset("This Month")} className="px-3 py-1 text-sm rounded bg-slate-100">
                This Month
              </button>
              <button onClick={() => onPreset("Last Month")} className="px-3 py-1 text-sm rounded bg-slate-100">
                Last Month
              </button>
              <button onClick={() => onPreset("This FY")} className="px-3 py-1 text-sm rounded bg-slate-100">
                This FY
              </button>
              <button onClick={fetchData} className="px-3 py-1 bg-indigo-600 text-white rounded">
                Refresh
              </button>
              <button onClick={() => setSettingsOpen((s) => !s)} className="px-3 py-1 bg-slate-200 rounded text-sm">
                ⚙️ Settings
              </button>
            </div>
          </div>

          {/* Settings panel */}
          {settingsOpen && (
            <div className="bg-white p-4 rounded shadow mb-4 grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* debounce */}
              <div>
                <label className="text-xs text-slate-600 block">Search debounce (ms)</label>
                <input
                  type="number"
                  min={0}
                  value={debounceMs}
                  onChange={(e) => setDebounceMs(Number(e.target.value || 0))}
                  className="w-full border px-2 py-1 rounded"
                />
              </div>
              {/* pdf scale */}
              <div>
                <label className="text-xs text-slate-600 block">PDF quality scale</label>
                <input
                  type="number"
                  min={1}
                  max={4}
                  step={0.5}
                  value={pdfScale}
                  onChange={(e) => setPdfScale(Number(e.target.value || 1))}
                  className="w-full border px-2 py-1 rounded"
                />
              </div>
              {/* orientation + page size */}
              <div>
                <label className="text-xs text-slate-600 block">PDF orientation</label>
                <select
                  value={pdfOrientation}
                  onChange={(e) => setPdfOrientation(e.target.value)}
                  className="w-full border px-2 py-1 rounded"
                >
                  <option value="landscape">Landscape</option>
                  <option value="portrait">Portrait</option>
                </select>
                <label className="text-xs text-slate-600 block mt-2">Page size</label>
                <select
                  value={pdfPageSize}
                  onChange={(e) => setPdfPageSize(e.target.value)}
                  className="w-full border px-2 py-1 rounded"
                >
                  <option value="a4">A4</option>
                  <option value="letter">Letter</option>
                </select>
                <div className="flex items-center gap-2 mt-2">
                  <input
                    id="persistSettings"
                    type="checkbox"
                    checked={persistSettings}
                    onChange={(e) => setPersistSettings(e.target.checked)}
                  />
                  <label htmlFor="persistSettings" className="text-sm">
                    Save settings to this browser
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Filters & exports */}
          <div className="bg-white p-4 rounded shadow mb-4 grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
            <div>
              <label className="text-xs text-slate-600">From</label>
              <input
                className="w-full border px-2 py-1 rounded"
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </div>
            <div>
              <label className="text-xs text-slate-600">To</label>
              <input
                className="w-full border px-2 py-1 rounded"
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={exportCsv} className="px-3 py-1 bg-emerald-600 text-white rounded">
                Export CSV
              </button>
              <button onClick={exportPdf} className="px-3 py-1 bg-neutral-700 text-white rounded">
                Export PDF
              </button>
              <button onClick={handlePrint} className="px-3 py-1 bg-gray-600 text-white rounded">
                Print
              </button>
            </div>
          </div>

          {/* Report content */}
          <div ref={reportRef} className="space-y-4">
            {/* Totals cards */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="bg-white rounded shadow p-4">
                <div className="text-sm text-slate-500">Total Income</div>
                <div className="text-2xl font-semibold text-green-600">{money(aggregated.totalIncome)}</div>
              </div>
              <div className="bg-white rounded shadow p-4">
                <div className="text-sm text-slate-500">Total Expense</div>
                <div className="text-2xl font-semibold text-rose-600">{money(aggregated.totalExpense)}</div>
              </div>
              <div className="bg-white rounded shadow p-4">
                <div className="text-sm text-slate-500">Net Profit</div>
                <div
                  className={`text-2xl font-semibold ${
                    aggregated.netProfit >= 0 ? "text-green-700" : "text-rose-700"
                  }`}
                >
                  {money(aggregated.netProfit)}
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  CGST: {money(aggregated.totalCgst)} · SGST: {money(aggregated.totalSgst)}
                </div>
              </div>
            </div>

            {/* Chart + quick stats */}
            <div className="bg-white rounded shadow p-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="col-span-2">
                <h3 className="text-sm font-semibold mb-2">Income vs Expense</h3>
                <div style={{ height: 260 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={[
                        { name: "Income", value: aggregated.totalIncome },
                        { name: "Expense", value: aggregated.totalExpense },
                      ]}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip formatter={(v) => money(v)} />
                      <Legend />
                      <Bar dataKey="value" name="Amount" fill="#0ea5a6" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div>
                <h3 className="text-sm font-semibold mb-2">Quick Stats</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <div className="text-xs text-slate-500">Invoices</div>
                    <div className="font-medium">{invoices.length}</div>
                  </div>
                  <div className="flex justify-between">
                    <div className="text-xs text-slate-500">Vouchers</div>
                    <div className="font-medium">{vouchers.length}</div>
                  </div>
                  <div className="flex justify-between">
                    <div className="text-xs text-slate-500">Accounts</div>
                    <div className="font-medium">{aggregated.income.length + aggregated.expense.length}</div>
                  </div>
                  <div className="text-xs text-slate-400 mt-2">
                    Debounce: {debounceMs} ms · PDF scale: {pdfScale} · Orientation:{" "}
                    {pdfOrientation.toUpperCase()} · Page: {pdfPageSize.toUpperCase()}
                  </div>
                </div>
              </div>
            </div>

            {/* Drilldown list */}
            <div className="bg-white rounded shadow p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold">Accounts & Drilldown</h3>
                <div className="flex items-center gap-2">
                  <input
                    aria-label="Search account"
                    placeholder="Search account"
                    value={searchAccount}
                    onChange={(e) => setSearchAccount(e.target.value)}
                    className="border px-2 py-1 rounded text-sm"
                  />
                  <button
                    onClick={() => {
                      setExpandedAccount(null);
                      setSearchAccount("");
                    }}
                    className="px-2 py-1 bg-slate-100 rounded text-sm"
                  >
                    Reset
                  </button>
                </div>
              </div>

              <div className="overflow-auto max-h-[40vh]">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="p-2 text-left">Account</th>
                      <th className="p-2 text-right">Type</th>
                      <th className="p-2 text-right">Amount</th>
                      <th className="p-2 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {masterRows.length === 0 && (
                      <tr>
                        <td colSpan={4} className="text-center p-4 text-slate-500">
                          No accounts
                        </td>
                      </tr>
                    )}
                    {masterRows.map((r) => (
                      <React.Fragment key={`${r.type}-${r.account}`}>
                        <tr className="border-t hover:bg-slate-50">
                          <td className="p-2">{r.account}</td>
                          <td className="p-2 text-right">{r.type}</td>
                          <td className="p-2 text-right">{money(r.amount)}</td>
                          <td className="p-2 text-right">
                            <button
                              onClick={() => setExpandedAccount(expandedAccount === r.account ? null : r.account)}
                              className="px-2 py-1 text-xs bg-slate-100 rounded"
                            >
                              Drill
                            </button>
                          </td>
                        </tr>

                        {expandedAccount === r.account && (
                          <tr>
                            <td colSpan={4} className="bg-slate-50 p-2">
                              <div className="text-xs text-slate-600 mb-2">
                                Details for <strong>{r.account}</strong>
                              </div>
                              <div className="space-y-2">
                                {/* Invoices */}
                                <div>
                                  <div className="text-xs font-semibold mb-1">Invoices</div>
                                  <div className="overflow-auto max-h-32">
                                    <table className="w-full text-xs">
                                      <thead>
                                        <tr>
                                          <th className="p-1">Date</th>
                                          <th className="p-1">Voucher</th>
                                          <th className="p-1">Revenue</th>
                                          <th className="p-1">Party</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {invoices
                                          .filter((inv) => {
                                            const txnClass = (
                                              inv.transactionClass ||
                                              inv.transactionclass ||
                                              inv.transaction ||
                                              ""
                                            ).toString();
                                            const t = (inv.type || "").toString().toLowerCase();
                                            const revenue =
                                              parseAmount(inv.totals?.gross ?? inv.gross ?? 0) -
                                              parseAmount(inv.totals?.discount ?? inv.discount ?? 0);
                                            if (r.type === "Income")
                                              return (t === "outward" || /supply|sale/i.test(txnClass)) && revenue > 0;
                                            if (r.type === "Expense")
                                              return (t === "inward" || /purchase|expense/i.test(txnClass)) && revenue > 0;
                                            return false;
                                          })
                                          .map((inv) => {
                                            const revenue =
                                              parseAmount(inv.totals?.gross ?? inv.gross ?? 0) -
                                              parseAmount(inv.totals?.discount ?? inv.discount ?? 0);
                                            return (
                                              <tr key={inv.id} className="border-t">
                                                <td className="p-1">{formatDate(inv.date)}</td>
                                                <td className="p-1">{inv.voucherNo || inv.voucher || "-"}</td>
                                                <td className="p-1 text-right">{money(revenue)}</td>
                                                <td className="p-1">{inv.partyName || inv.party || "-"}</td>
                                              </tr>
                                            );
                                          })}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>

                                {/* Vouchers */}
                                <div>
                                  <div className="text-xs font-semibold mt-2 mb-1">Journal / Vouchers</div>
                                  <div className="overflow-auto max-h-32">
                                    <table className="w-full text-xs">
                                      <thead>
                                        <tr>
                                          <th className="p-1">Date</th>
                                          <th className="p-1">Voucher</th>
                                          <th className="p-1">Dr/Cr</th>
                                          <th className="p-1">Amount</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {vouchers
                                          .flatMap((v) =>
                                            (v.rows || []).map((row, idx) => ({ parent: v, row, idx }))
                                          )
                                          .filter(({ row }) => {
                                            const label = row.partyName || row.account || row.ledger || "Journal";
                                            return label === r.account;
                                          })
                                          .map(({ parent, row }, i) => (
                                            <tr key={i} className="border-t">
                                              <td className="p-1">{formatDate(parent.date)}</td>
                                              <td className="p-1">{parent.voucherNo || parent.voucher || "-"}</td>
                                              <td className="p-1">{row.drcr}</td>
                                              <td className="p-1 text-right">
                                                {money(parseAmount(row.debit || row.credit || 0))}
                                              </td>
                                            </tr>
                                          ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="text-xs text-slate-500">
              <p>
                <strong>Notes:</strong> Client-side heuristics used. For precise classification add ledger/account ids
                to your documents.
              </p>
            </div>
          </div>
        </div>
      </main>

      {/* loading & error */}
      {loading && (
        <div className="fixed inset-0 bg-black/10 flex items-center justify-center z-50">
          <div className="bg-white p-4 rounded shadow">Loading...</div>
        </div>
      )}
      {error && (
        <div className="fixed bottom-4 right-4 bg-rose-600 text-white px-3 py-2 rounded shadow">{error}</div>
      )}
    </div>
  );
}
